import type { CSSProperties } from 'react'

/**
 * The inline style for one `stagger-item` (globals.css): its place in the
 * list, from which the item's delay follows — 40ms a step, capped at 320ms.
 * `--i` is a bare number on purpose; the CSS multiplies it by the step.
 */
export function staggerStyle(index: number): CSSProperties {
  return { ['--i' as string]: index }
}

/**
 * The same step as an animation delay, for an element entering on its own
 * `animate-*` class rather than as a `stagger` child: no rule reads `--i`
 * there. Inline, off the tokens, so reduced motion's `animation-delay` clamp
 * still reaches it.
 */
export function staggerDelayStyle(index: number): CSSProperties {
  return {
    animationDelay: `min(${String(index)} * var(--motion-stagger-step), var(--motion-stagger-cap))`,
  }
}

/**
 * How long a `stagger` container's entrance can run: the last step's delay
 * (`--motion-stagger-cap`) plus one fade-up (`--motion-slow`). Before this,
 * a child that joins late — a section whose cached read reached it a frame
 * after the page — still enters with the rest.
 */
const STAGGER_ENTRANCE_MS = 320 + 480

const isCssAnimation = (animation: Animation): animation is CSSAnimation =>
  'animationName' in animation

/** A CSS animation that will end: not a looping shimmer, pulse or caret, which never does. */
const isFiniteEntrance = (animation: Animation): animation is CSSAnimation =>
  isCssAnimation(animation) &&
  animation.playState !== 'finished' &&
  animation.effect?.getComputedTiming().iterations !== Infinity

/** The children still fading up: the stagger's own entrance. */
const fadingUpChildren = (container: HTMLElement): Animation[] =>
  Array.from(container.children).flatMap((child) =>
    // jsdom has no Web Animations: nothing is animating there to wait for.
    typeof child.getAnimations === 'function'
      ? child
          .getAnimations()
          .filter(
            (animation) =>
              isCssAnimation(animation) &&
              animation.animationName === 'fade-up' &&
              animation.playState !== 'finished',
          )
      : [],
  )

/** Every entrance still running anywhere under the container, the marks inside its items too. */
const enteringDescendants = (container: HTMLElement): Animation[] =>
  typeof container.getAnimations === 'function'
    ? container.getAnimations({ subtree: true }).filter(isFiniteEntrance)
    : []

/**
 * Ends a `stagger` container's entrance once it has played, by marking the
 * container `data-entered` — the attribute the `stagger` rule in globals.css
 * stops at. Without it the rule holds for the container's whole life, and
 * every child that mounts later fades up as if the page had just arrived: a
 * stock tab's panel on each `?tab=` click, a card when its data lands.
 *
 * Waits for the entrance's full length, then for any child still fading up
 * (a late joiner, or a frame that started late on a busy main thread), so
 * the mark never cuts an animation short — dropping a running animation's
 * rule ends it where it stands. Returns the cleanup for the effect that
 * starts it.
 *
 * `subtree` waits for every finite animation under the container as well, for
 * a container whose mark also ends its items' own entrances: DataTable's body,
 * where `data-entered` stops a cell's bar or sparkline replaying (globals.css),
 * and so must not land while a sparkline is still drawing in.
 *
 * Set on the element directly rather than through React state: it changes
 * nothing React renders, and a state change would re-render the rail for
 * nothing. React leaves an attribute it does not manage alone.
 */
export function closeStaggerAfterEntrance(
  container: HTMLElement,
  { subtree = false }: { subtree?: boolean } = {},
): () => void {
  let cancelled = false

  const close = () => {
    if (cancelled) return
    const entering = subtree ? enteringDescendants(container) : fadingUpChildren(container)
    if (entering.length === 0) {
      container.setAttribute('data-entered', '')
      return
    }
    // `allSettled`: an animation cancelled under us (its child unmounted)
    // rejects `finished`, and that one is done too.
    void Promise.allSettled(entering.map((animation) => animation.finished)).then(close)
  }

  const timer = window.setTimeout(close, STAGGER_ENTRANCE_MS)
  return () => {
    cancelled = true
    window.clearTimeout(timer)
  }
}
